import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Legend, CartesianGrid, Tooltip, LabelList } from "recharts";
import styles from "./css/BarsGraph.module.css";
import { LoadingWaves } from '../addons/LoadingWaves';

interface BarsGraphProps {
  data: {
    [month: string]: {
      [key: string]: number;
    };
  } | undefined;
  colors: {
    [key: string]: string;
  };
  isStacked?: boolean;
  legendPosition?: 'center' | 'right';
}

interface TooltipPayload {
  dataKey: string;
  fill: string;
  name: string;
  value: number;
}

interface ChartDataItem {
  month: string;
  [key: string]: string | number;
}

interface ChartItem {
  month: string;
  [key: string]: number | string;
}

export const BarsGraph: React.FC<BarsGraphProps> = ({ data, colors, isStacked = true, legendPosition = 'center' }) => {
  const [horizontalGridPoints, setHorizontalGridPoints] = useState<number[]>([]);
  const [barSize, setBarSize] = useState(15);
  let tooltipKey: string | null = null;

  // Get keys in the same order as the colors object
  const dataKeys = Object.keys(colors);
  const isSingleKey = dataKeys.length === 1;
  const showLegend = !isSingleKey && dataKeys.length > 1;

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1536px)'); // 2xl breakpoint

    const updateGridPoints = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        // 2xl screen size (larger screens)
        if(showLegend){
            setHorizontalGridPoints(Array.from({ length: 14 }, (_, i) => 45 + i * 12));
        }else{
            setHorizontalGridPoints(Array.from({ length: 18 }, (_, i) => -3 + i * 12));
        }
      } else {
        // default screen size (smaller screens)
        if(showLegend){
            setHorizontalGridPoints(Array.from({ length: 10 }, (_, i) => 63 + i * 12));
        }else{
            setHorizontalGridPoints(Array.from({ length: 15 }, (_, i) =>  i * 12));
        }
      }
    };

    // Initial check
    updateGridPoints(mediaQuery);

    // Add listener for screen size changes
    mediaQuery.addEventListener('change', updateGridPoints);

    // Cleanup
    return () => mediaQuery.removeEventListener('change', updateGridPoints);
  }, [showLegend]);

  useEffect(() => {
    // Calculate bar size based on number of data keys
    const numBars = dataKeys.length;
    let newBarSize = 15; // default size

    if (numBars === 1) {
      newBarSize = 30;
    } else if (numBars >= 2 && numBars <= 4) {
      newBarSize = 25;
    } else {
      newBarSize = 15;
    }

    setBarSize(newBarSize);
  }, [dataKeys.length]);

  // Normalize data to include all keys with 0 for missing values
  const chartData: ChartItem[] = data ? Object.entries(data).map(([month, values]) => ({
    month,
    ...Object.fromEntries(dataKeys.map(key => [key, values[key] || 0]))
  })) : [];
  
  // Find the max value including totals
  const allValues = chartData.flatMap(item => {
    const individualValues = dataKeys.map(key => Number(item[key] || 0));
    const total = individualValues.reduce((sum, val) => sum + val, 0);
    return [...individualValues, total];
  });
  
  const maxValue = Math.max(...(allValues.length > 0 ? allValues : [100]));
  
  // Round up maxValue 
  const roundNumber = (num: number) => {
    const pow10 = Math.pow(10, Math.floor(Math.log10(num)));
    return Math.ceil(num / pow10) * pow10;
  };
  
  const displayMaxValue = roundNumber(maxValue * 1.1); // Add 10% padding
  
  // Modified scaling approach - now we keep original values
  const displayData = chartData.map(item => {
    const newItem: ChartDataItem = { ...item };
    
    // Calculate total
    const total = dataKeys.reduce((sum, key) => sum + Number(item[key] || 0), 0);
    newItem.total = total;
    
    // Store original values for tooltip
    dataKeys.forEach(key => {
      newItem[`${key}_original`] = item[key];
      newItem[key] = item[key]; // Keep original value
    });
    
    return newItem;
  });

  const renderLegend = (props: any) => {
    return (
      <ul className="flex flex-wrap justify-center gap-2 max-w-[300px] mx-auto">
        {dataKeys.map((entry: string, index: number) => (
          <li key={`item-${index}`} className="flex items-center">
            <span 
              className="inline-block w-1.5 h-1.5 rounded-full" 
              style={{ backgroundColor: colors[entry] }}
            />
            <span className="mr-1 text-navyBlue text-[10px]">
              {entry}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  // Custom tooltip formatter that uses the original values
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    
    const key = isSingleKey ? dataKeys[0] : tooltipKey;
    
    if (!key) return null;
    
    if (isSingleKey) {
      const originalValue = payload[0].payload[`${key}_original`];
      
      return (
        <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
          <p className="text-sm font-medium mb-1">{label}</p>
          <div className="flex items-center gap-2 text-sm">
            <span 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: colors[key] }}
            />
            <span>{key}:</span>
            <span className="font-medium">{formatNumber(originalValue)}</span>
          </div>
        </div>
      );
    }
    
    // Original multiple keys tooltip logic
    const barData = payload.find((bar: TooltipPayload) => 
      bar.dataKey === key
    );
    
    if (!barData) return null;
    
    const originalValue = payload.find((p: any) => 
      p.dataKey === `${key}_original`
    )?.value || barData.value;
    
    return (
      <div className="bg-white p-2 border border-gray-200 rounded shadow-sm">
        <p className="text-sm font-medium mb-1">{label}</p>
        <div className="flex items-center gap-2 text-sm">
          <span 
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: barData.fill }}
          />
          <span>{barData.name}:</span>
          <span className="font-medium">{formatNumber(originalValue)}</span>
        </div>
      </div>
    );
  };

  // Find the max value from original data
  const originalMax = Math.max(...chartData.flatMap(item => 
    dataKeys.map(key => Number(item[key] || 0))
  ));

  // Generate Y-axis ticks with nice numbers
  const generateNiceTicks = (max: number, tickCount: number = 5) => {
    const ticks = [];
    const step = max / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      ticks.push(Math.round(i * step));
    }
    return ticks;
  };

  const yTicks = generateNiceTicks(displayMaxValue);

  const formatNumber = (value: number) => {
    if (typeof value !== 'number') return value;
    
    // Round only if there are decimal places
    let roundedValue = Number.isInteger(value) ? value : Number(value.toFixed(3));
    
    // Format with K/M for large numbers
    const absValue = Math.abs(roundedValue);
    
    if (absValue >= 1000000) {
      const millions = roundedValue / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    }
    
    if (absValue >= 1000) {
      const thousands = roundedValue / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    
    if (absValue < 1 && absValue > 0) {
      return roundedValue.toFixed(2);
    }
    
    return roundedValue.toString();
  };

  // If no data, show loading waves with first color
  if (!data || Object.keys(data).length === 0) {
    return (
      <div className={styles.container}>
        <LoadingWaves color={colors[dataKeys[0]]} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ResponsiveContainer width="100%">
        <BarChart 
          key={JSON.stringify(displayData)}
          data={displayData} 
          margin={{ top:5, right: 0, left: -25, bottom: 20 }}
          barGap={8}
          barCategoryGap={40}
        >
          <CartesianGrid 
            horizontal={true}
            vertical={false}
            strokeDasharray="0"
            stroke="#DADEE5"
            strokeWidth={1}
            horizontalPoints={horizontalGridPoints}
          />
          <XAxis 
            dataKey="month" 
            axisLine={false}
            tickLine={false}
            tick={{ 
              fill: '#6C7693', 
              fontSize: 10
            }}
            tickMargin={8}
          />
          <YAxis 
            hide={false}
            ticks={yTicks}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6C7693', fontSize: 10 }}
            width={50}
            domain={[0, displayMaxValue]}
            tickFormatter={formatNumber}
            scale="linear"
            padding={{ top: 20, bottom: 20 }}
          />
          <Tooltip 
            content={CustomTooltip}
            cursor={{ fill: 'transparent' }}
          />
          {[...dataKeys].reverse().map((key, index) => (
            <React.Fragment key={key}>
              {isSingleKey ? (
                <Bar
                  dataKey={key}
                  fill={colors[key]}
                  radius={[4, 4, 0, 0]}
                  barSize={barSize}
                  isAnimationActive={false}
                  className={styles.bar}
                  onMouseOver={() => { tooltipKey = key; }}
                  onMouseLeave={() => { tooltipKey = null; }}
                >
                  <LabelList
                    dataKey="total"
                    position="top"
                    fill="#6C7693"
                    fontSize={10}
                    offset={10}
                    formatter={formatNumber}
                  />
                </Bar>
              ) : (
                <>
                  <Bar
                    dataKey={`${key}_original`}
                    fill="transparent"
                    stackId="original"
                    radius={[0, 0, 0, 0]}
                    barSize={0}
                    isAnimationActive={false}
                  />
                  <Bar
                    dataKey={key}
                    fill={colors[key]}
                    stackId={isStacked ? "stack" : undefined}
                    radius={index === 0 || index === dataKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    barSize={barSize}
                    isAnimationActive={false}
                    className={styles.bar}
                    onMouseOver={() => { tooltipKey = key; }}
                    onMouseLeave={() => { tooltipKey = null; }}
                  >
                    {index === dataKeys.length - 1 && (
                      <LabelList
                        dataKey="total"
                        position="top"
                        fill="#6C7693"
                        fontSize={10}
                        offset={10}
                        formatter={formatNumber}
                      />
                    )}
                  </Bar>
                </>
              )}
            </React.Fragment>
          ))}
          {showLegend && (
            <Legend 
              content={renderLegend}
              verticalAlign="top"
              align={legendPosition === 'center' ? 'center' : 'right'}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};